import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { jwtDecode } from "jwt-decode";
import { throwError } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { LocalStorage } from "src/libs/storage/src";
import { StorageKey } from "../enums/storage-key";
import { UserRole } from "../enums/user-role";
import { IAccessTokenPayload, IAuthTokens } from "../models/auth/auth-tokens";
import { IResetPasswordPayload } from "../models/auth/reset-password-payload";
import { ISignInPayload, ISignInWithLoginPayload } from "../models/auth/sign-in-payload";
import { ISignUpPayload } from "../models/auth/sign-up-payload";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  constructor(private readonly _httpClient: HttpClient, private readonly _localStorage: LocalStorage) {}

  getAccessToken(): string | null {
    return this._localStorage.getItem(StorageKey.AccessToken);
  }

  isAuthorized(): boolean {
    const accessToken = this.getAccessToken();
    const refreshTokenExpiresAt = this.getRefreshTokenExpirationDate();

    if (accessToken && refreshTokenExpiresAt) {
      return !this.isTokenExpired(refreshTokenExpiresAt);
    }

    return false;
  }

  isClientRole(): boolean {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      return false;
    }

    const { role } = jwtDecode<IAccessTokenPayload>(accessToken);

    return role === UserRole.Client;
  }

  isAccessTokenExpired(): boolean {
    const accessToken = this.getAccessToken();
    const accessTokenExpiresAt = this.getAccessTokenExpirationDate();

    return !accessToken || this.isTokenExpired(accessTokenExpiresAt);
  }

  private isTokenExpired(tokenExpiresAt: string | null): boolean {
    if (!tokenExpiresAt) {
      return true;
    }

    return Date.now() > new Date(tokenExpiresAt).getTime();
  }

  async signIn(payload: ISignInPayload): Promise<void> {
    const authTokens = await this._httpClient
      .post<IAuthTokens>(this.withBaseUrl("/sign-in-with-email"), payload)
      .toPromise();

    this.setAuthTokens(authTokens);
  }

  async signInWithLogin(payload: ISignInWithLoginPayload): Promise<void> {
    const authTokens = await this._httpClient.post<IAuthTokens>(this.withBaseUrl("/sign-in"), payload).toPromise();

    this.setAuthTokens(authTokens);
  }

  async signUp(payload: ISignUpPayload): Promise<void> {
    const authTokens = await this._httpClient
      .post<IAuthTokens>(this.withBaseUrl("/sign-up"), payload)
      .toPromise();

    this.setAuthTokens(authTokens);
    this._localStorage.removeItem(StorageKey.HasSeenTrialSubscriptionModal);
  }

  sendEmailConfirmation(email: string) {
    return this._httpClient.get<void>(this.withBaseUrl("/email-confirm"), { params: { email } });
  }

  confirmEmail(token: string, email: string) {
    return this._httpClient.post<void>(this.withBaseUrl("/email-confirm"), { email, token });
  }

  requestPasswordReset(email: string) {
    return this._httpClient.get<void>(this.withBaseUrl("/password-reset"), { params: { email } });
  }

  resetPassword(payload: IResetPasswordPayload) {
    return this._httpClient.post<void>(this.withBaseUrl("/password-reset"), payload);
  }

  refreshAuthTokens() {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    const refreshTokenExpiresAt = this.getRefreshTokenExpirationDate();

    if (!accessToken || !refreshToken || this.isTokenExpired(refreshTokenExpiresAt)) {
      return throwError(new Error("No tokens available for refresh"));
    }

    return this._httpClient
      .post<IAuthTokens>(this.withBaseUrl("/refresh"), { accessToken, refreshToken })
      .pipe(map(authTokens => this.setAuthTokens(authTokens)));
  }

  async signOut(): Promise<void> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      throw new Error("Refresh token is missing, cannot sign out");
    }

    await this._httpClient
      .delete(this.withBaseUrl("/logout"), { body: { refreshToken } })
      .toPromise()
      .finally(() => this.removeAuthTokens());
  }

  private getRefreshToken(): string | null {
    return this._localStorage.getItem(StorageKey.RefreshToken);
  }

  private getAccessTokenExpirationDate(): string | null {
    return this._localStorage.getItem(StorageKey.AccessTokenExpiresAt);
  }

  private getRefreshTokenExpirationDate(): string | null {
    return this._localStorage.getItem(StorageKey.RefreshTokenExpiresAt);
  }

  private setAuthTokens(authTokens: IAuthTokens): void {
    this._localStorage.setItem(StorageKey.AccessToken, authTokens.accessToken);
    this._localStorage.setItem(StorageKey.RefreshToken, authTokens.refreshToken);
    this._localStorage.setItem(StorageKey.AccessTokenExpiresAt, authTokens.accessTokenExpiresAt);
    this._localStorage.setItem(StorageKey.RefreshTokenExpiresAt, authTokens.refreshTokenExpiresAt);
  }

  private removeAuthTokens(): void {
    this._localStorage.removeItem(StorageKey.HasSeenTrialSubscriptionModal);
    this._localStorage.removeItem(StorageKey.AccessToken);
    this._localStorage.removeItem(StorageKey.RefreshToken);
    this._localStorage.removeItem(StorageKey.AccessTokenExpiresAt);
    this._localStorage.removeItem(StorageKey.RefreshTokenExpiresAt);
  }

  private withBaseUrl(endpointUrl: string) {
    return `${environment.serverUrl}/auth${endpointUrl}`;
  }
}
